import { Link } from "react-router-dom";
import { Section, Step, Callout, KeyTerm, TOC, Pill } from "./HelpComponents";

const TOC_ITEMS = [
  { id: "setup",       title: "Team setup" },
  { id: "branding",    title: "Branding & color theme" },
  { id: "modules",     title: "Module configuration" },
  { id: "weights",     title: "Readiness engine weights" },
  { id: "calendar",    title: "Annual protocol calendar" },
  { id: "onboarding",  title: "Onboarding players" },
  { id: "daily-ops",   title: "Day-to-day operations" },
  { id: "data-flow",   title: "Where the data comes from" },
  { id: "readiness",   title: "Reading the Readiness Score" },
  { id: "status",      title: "Status badges & Auto-Hold" },
  { id: "workload",    title: "Acute:Chronic workload" },
  { id: "hybrid",      title: "Hybrid Auto-Suggest" },
  { id: "alerts",      title: "Critical alerts" },
  { id: "troubleshoot",title: "Troubleshooting" },
];

export default function CoachGuide() {
  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-10" data-testid="coach-guide">
      <article className="space-y-12 max-w-3xl">
        <header>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Coach Handbook · v2.7</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white mt-2">How to run ArmIQ as a coach</h1>
          <p className="text-base text-white/60 mt-3 max-w-2xl">
            Everything you need from the moment you sign up to the moment you make a Sunday-evening
            call on who throws which hybrid Monday morning. Read it once cover-to-cover — under 15 minutes.
          </p>
        </header>

        <Section id="setup" kicker="Step 1" title="Set up your team">
          <p>
            ArmIQ is multi-tenant — each team is its own isolated workspace. You set the workspace up the
            first time you sign in.
          </p>
          <Step n={1} title="Create your coach account">
            On the landing page click <em>Get Started</em> → fill in your name, email, password, and a
            <strong> team name</strong> (this is the workspace name your players will see). You can sign up
            with email/password or with Google.
          </Step>
          <Step n={2} title="Verify you land on the Coach Console">
            After sign-up you're routed to <code className="text-white">/dashboard</code> — the Overview
            page. The sidebar shows your <Pill color="#D90429">Tenant</Pill> name and 6 sections:
            <em> Overview, Roster, Calendar, Routine, CSV Import, Settings</em>.
          </Step>
          <Step n={3} title="Open Settings and complete brand + modules">
            Visit <code className="text-white">/dashboard/settings</code>. Two pillar configurations:
            <strong> Brand</strong> (your colors, logo on staff emails) and <strong>Modules</strong>
            (which baseline and daily-log fields are enabled for your team).
          </Step>
          <Callout kind="info" title="Multi-tenant guarantee">
            No other team can see your roster, logs, or settings. Every API call is scoped to your
            <code className="text-white">team_id</code> server-side.
          </Callout>
        </Section>

        <Section id="branding" kicker="Step 2" title="Make ArmIQ look like your program">
          <p>
            Pick a <em>Primary</em> color (your program's dominant color — used for nav highlights and the
            top header), a <em>Secondary</em> color (action color — used for CTAs and the alerts), and an
            <em> Accent</em> color (chart highlights). Save. Everything in the app — including the player
            portal your athletes see — re-skins instantly.
          </p>
          <Callout kind="warn" title="Contrast matters">
            Stick to colors that have decent contrast against a dark navy background; ArmIQ's chrome is
            dark by default. Pure black or near-black colors will disappear.
          </Callout>
        </Section>

        <Section id="modules" kicker="Step 3" title="Module configuration (what you track)">
          <p>
            ArmIQ has two configurable surfaces: <strong>Baseline metrics</strong> (Module 1 — collected once
            at the start of the year per athlete) and <strong>Daily log fields</strong> (Module 4 — collected
            every training day from the athlete's phone).
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <KeyTerm term="Baseline (Module 1)">
              ROM (ext/int/total arc), grip 90/90 LSI, ER/IR strength, Y-Balance, broad jump, vertical,
              60-yd splits, 5-10-5 / L-Drill, pull-down mph, mound velo, FlightScope/Rapsodo links.
              Toggle any field off per team.
            </KeyTerm>
            <KeyTerm term="Daily log (Module 4)">
              Arm Health, Body Soreness, Sleep Quality, Mental Energy, scheduled Hybrid, throwing
              environment, max distance, pitch count, 75% accuracy %, pain during, post-throw fatigue.
            </KeyTerm>
          </div>
          <p>
            Whatever you toggle <strong>OFF</strong> simply doesn't appear to your athletes — saves you
            collecting noise.
          </p>
        </Section>

        <Section id="weights" kicker="Step 4" title="Readiness engine weights">
          <p>
            On the Settings page, scroll to <strong>Readiness Engine Weights</strong>. Four sliders
            control how much each component contributes to the daily 0–100 Readiness Score:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <KeyTerm term="Recovery (HRV vs baseline)">
              7-day HRV mean compared to the prior 7-day baseline. Default <strong>40%</strong>.
            </KeyTerm>
            <KeyTerm term="Sleep">
              7-day mean sleep hours vs your team target (Settings → Thresholds). Default <strong>30%</strong>.
            </KeyTerm>
            <KeyTerm term="Workload (A:C ratio)">
              Acute-to-chronic ratio of throws + workout minutes. Sweet spot 0.8–1.3. Default <strong>20%</strong>.
            </KeyTerm>
            <KeyTerm term="Subjective">
              Mean of arm health + sleep quality + mental energy from the most recent daily log. Default <strong>10%</strong>.
            </KeyTerm>
          </div>
          <Callout kind="info" title="Numbers don't have to sum to 100">
            The engine auto-normalises whatever you set to add to 100 at score time. Setting 50/25/15/10
            is equivalent to 50/25/15/10.
          </Callout>
        </Section>

        <Section id="calendar" kicker="Step 5" title="Customize the annual protocol calendar">
          <p>
            <code className="text-white">/dashboard/calendar</code> shows your team's annual phase plan.
            By default you inherit the platform's 7-phase model (Summer Base Build → Extension → Early
            Fall Mound Intro → Late Fall Velocity → Off-Season → Pre-Season Ramp → In-Season).
          </p>
          <Step n={1} title="Edit a phase">
            Click <em>Edit calendar</em> → every row becomes editable: phase name, block label, hybrid
            letter, protocol text, rest pattern, success metrics, and a color swatch (drives the
            left-border accent).
          </Step>
          <Step n={2} title="Add or remove phases">
            Use <em>Add phase</em> or the trash icon. Phase ordering follows the <code>order</code>
            field — changing input values doesn't rearrange rows automatically.
          </Step>
          <Step n={3} title="Save or reset">
            <em>Save calendar</em> pushes the override to your team (PUT <code>/api/teams/me/calendar</code>);
            <em> Reset to default</em> wipes the override and falls back to the platform default.
          </Step>
        </Section>

        <Section id="onboarding" kicker="Step 6" title="Onboarding players">
          <p>You have <strong>three</strong> ways to add athletes to your team. Pick what fits your workflow.</p>
          <Step n="A" title="Invite link (recommended)">
            <strong>Roster → + Add Player → Invite Link</strong>. Fill in name + optional details, click
            <em> Generate</em>. ArmIQ copies the URL to your clipboard. Text or email it to the athlete.
            They open the link, set their email + password, and are auto-signed in to the portal —
            already attached to your team.
          </Step>
          <Step n="B" title="Coach-created password">
            <strong>Roster → + Add Player → Password</strong>. Use this if you want to hand the athlete
            their credentials in person. Initial password defaults to <code>armiq123</code>; they can
            change it later.
          </Step>
          <Step n="C" title="CSV import">
            <strong>CSV Import</strong> in the sidebar. Drop a CSV with columns <code>name, email,
            year, dominant_arm, position</code>. Useful when you're seeding a whole team at the start
            of the season.
          </Step>
          <Callout kind="good" title="Invite-link details">
            Invites are tokenised, single-use, and expire after 30 days. You can see all pending invites
            and re-copy or revoke any of them from the Roster page.
          </Callout>
        </Section>

        <Section id="daily-ops" kicker="Step 7" title="What you do every day">
          <p>The Coach Console is built around three views:</p>
          <Step n={1} title="Overview (`/dashboard`)">
            KPIs at a glance: roster size, average readiness, count of CRITICAL athletes, players on
            HOLD, total throws this week, plus a leaderboard of "lowest readiness" athletes — exactly
            the kids you should pull aside in the locker room.
          </Step>
          <Step n={2} title="Roster (`/dashboard/roster`)">
            Live grid of every athlete. Columns: name · position · year · 7-day volume ·
            <strong> readiness score</strong> · <strong>suggested hybrid</strong> · status badge ·
            delete. The Suggested column is the engine's recommendation for today (see
            <a href="#hybrid" className="text-[var(--brand-secondary)] underline ml-1">Hybrid Auto-Suggest</a>).
          </Step>
          <Step n={3} title="Player Detail (`/dashboard/player/<id>`)">
            The deep dive. Five tabs:
            <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
              <li><strong>Demographics & Baseline</strong> — Module 1 fields (editable in place)</li>
              <li><strong>Readiness</strong> — today's score breakdown by component, plus the 14-day sparkline trend</li>
              <li><strong>Workload</strong> — Acute Workload 14-day area chart + 7-day rollup totals</li>
              <li><strong>Recovery</strong> — HRV / resting HR / sleep telemetry from Apple Health & Health Connect</li>
              <li><strong>Pitch Data</strong> — FlightScope / Rapsodo ingestion (paste JSON or upload .json)</li>
            </ul>
          </Step>
        </Section>

        <Section id="data-flow" kicker="Where it comes from" title="The full data flow">
          <p>Four streams feed your dashboard:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <KeyTerm term="① Daily Self-Log">
              Athlete opens the player portal (their phone), takes 30 seconds to slide 4 ratings + select
              today's hybrid + environment. Submits. Backend computes status (READY / CAUTION / CRITICAL).
            </KeyTerm>
            <KeyTerm term="② Apple Health / Health Connect">
              Athlete pairs their iPhone or Android phone once (Portal → Biometrics → copy token → paste
              into the companion app). Each day the native app pushes HRV, resting HR, sleep, VO₂ Max,
              and workouts to <code>/api/ingest/apple-health</code>.
            </KeyTerm>
            <KeyTerm term="③ FlightScope / Rapsodo">
              Coach pastes JSON exports (or uploads .json files) on the Player Detail page. The backend
              parses both schemas and updates the athlete's mound velocity profile field.
            </KeyTerm>
            <KeyTerm term="④ CSV roster import">
              Used at season start to seed the team. Manual updates beyond that go through the Roster
              UI.
            </KeyTerm>
          </div>
        </Section>

        <Section id="readiness" kicker="Decoding the data" title="Reading the Readiness Score (0–100)">
          <p>
            One number, calibrated daily, that says <em>"how prepared is this athlete to throw?"</em>.
            It's the weighted sum of four sub-scores (see <a href="#weights" className="text-[var(--brand-secondary)] underline">Weights</a>):
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">PEAK</span><Pill color="#16A34A">≥ 80</Pill></div>
              <div className="text-sm text-white/60 mt-1">Green light. Push hybrid A or B without hesitation.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">GOOD</span><Pill color="#22D3EE">65–79</Pill></div>
              <div className="text-sm text-white/60 mt-1">Solid day. Hybrid B is the safe default.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">CAUTION</span><Pill color="#F59E0B">45–64</Pill></div>
              <div className="text-sm text-white/60 mt-1">Investigate. Open the player detail. Maybe drop to C.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <div className="flex items-center justify-between"><span className="font-display font-bold text-white">RISK</span><Pill color="#EF4444">&lt; 45</Pill></div>
              <div className="text-sm text-white/60 mt-1">No throws today. Hybrid E — ROM only.</div>
            </div>
          </div>
          <Callout kind="info" title="Where to see it">
            Roster table column · player detail header · 14-day sparkline trend on the Readiness tab ·
            Overview lowest-readiness leaderboard.
          </Callout>
        </Section>

        <Section id="status" kicker="Decoding the data" title="Status badges & Auto-Hold">
          <p>Each daily log lands in one of three statuses, computed server-side:</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg ink-card p-4">
              <Pill color="#16A34A">READY</Pill>
              <div className="text-sm text-white/70 mt-2">No flagged sub-scores. Athlete is good to run prescribed track.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <Pill color="#F59E0B">CAUTION / HOLD</Pill>
              <div className="text-sm text-white/70 mt-2">HRV-Auto-Hold triggered, or readiness fell below the team's threshold.</div>
            </div>
            <div className="rounded-lg ink-card p-4">
              <Pill color="#EF4444">CRITICAL</Pill>
              <div className="text-sm text-white/70 mt-2">arm_health = 1 OR pain_during = true. Auto-emails staff via Resend.</div>
            </div>
          </div>
          <Callout kind="warn" title="Auto-Hold is non-overridable">
            If HRV trend or A:C ratio breaches the team threshold, the athlete sees a yellow "HRV Auto-Hold"
            chip on their portal and the coach roster status flips to CAUTION — even if the subjective
            sliders look fine. The body is telling us something.
          </Callout>
        </Section>

        <Section id="workload" kicker="Decoding the data" title="Acute:Chronic workload ratio">
          <p>
            Industry standard fatigue model. <strong>Acute</strong> = total throws in the last 7 days.
            <strong> Chronic</strong> = average weekly throws over the last 28 days. Ratio = acute / chronic.
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li><strong>0.8–1.3</strong>: optimal — sustainable training.</li>
            <li><strong>&lt; 0.8</strong>: detraining — they're underloaded.</li>
            <li><strong>&gt; 1.3</strong>: spike — risk of soft-tissue injury rises sharply.</li>
            <li><strong>&gt; 2.0 or &lt; 0.4</strong>: extreme; the engine zeros out the load component entirely.</li>
          </ul>
          <p>You can shift the safe corridor per team in Settings → Thresholds.</p>
        </Section>

        <Section id="hybrid" kicker="Closed-loop" title="Hybrid Auto-Suggest">
          <p>
            The engine maps today's Readiness Score to a recommended throwing track. The athlete sees
            their suggestion as a colored chip in their portal — pre-selected in the daily log — and you
            see the same chip in the Roster table.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#D90429" }}>A</div>
              <div className="text-xs text-white/60 mt-1">≥ 80 · Long toss + Pull-downs</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#F59E0B" }}>B</div>
              <div className="text-xs text-white/60 mt-1">60–79 · Long toss no pull-downs</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#22D3EE" }}>C</div>
              <div className="text-xs text-white/60 mt-1">40–59 · ROM &amp; light toss</div>
            </div>
            <div className="rounded-lg ink-card p-4 text-center">
              <div className="font-display font-black text-3xl" style={{ color: "#16A34A" }}>D</div>
              <div className="text-xs text-white/60 mt-1">&lt; 40 OR critical · ROM only</div>
            </div>
          </div>
          <Callout kind="info" title="Athletes can override">
            The suggestion is pre-selected but not enforced. If you've told the athlete in person to
            push a different hybrid, they can change it before submitting their log. You'll see the
            actual hybrid they ran on their Player Detail.
          </Callout>
        </Section>

        <Section id="alerts" kicker="When things go red" title="Critical alerts">
          <p>
            When an athlete logs <strong>arm_health = 1</strong> ("severe pain") OR <strong>pain during
            throwing = true</strong>, ArmIQ:
          </p>
          <ol className="list-decimal list-inside ml-2 space-y-1">
            <li>Stamps the log as <Pill color="#EF4444">CRITICAL</Pill>.</li>
            <li>Fires an immediate email via <strong>Resend</strong> to every address in your team's
              <code> staff_emails</code> array (set in Settings → Staff).</li>
            <li>Shows the athlete a banner: "Training room — see athletic trainer before next throw".</li>
            <li>Auto-suggests Hybrid E for the next session.</li>
          </ol>
          <Callout kind="danger" title="Domain verification required for delivery">
            For emails to actually deliver, your sender domain must be verified on Resend. Until that's
            done, alerts are written to the audit log only. (See PRD next-actions.)
          </Callout>
        </Section>

        <Section id="troubleshoot" kicker="Quick fixes" title="Troubleshooting">
          <div className="grid sm:grid-cols-2 gap-3">
            <KeyTerm term="An athlete's readiness is stuck">
              They probably haven't synced Apple Health / Health Connect yet. Walk them through
              <Link to="/portal/apple-health" className="text-[var(--brand-secondary)] ml-1 underline">Portal → Biometrics</Link>.
            </KeyTerm>
            <KeyTerm term="Invite link 'expired'">
              30-day expiry. Issue a new one from Roster.
            </KeyTerm>
            <KeyTerm term="Color theme didn't update">
              Hard-refresh the page. The brand context only re-reads colors on full page load.
            </KeyTerm>
            <KeyTerm term="CSV import said 'malformed row'">
              Check for a trailing blank line or an unescaped comma in a position name like "RP, closer".
              Wrap that cell in double quotes.
            </KeyTerm>
          </div>
        </Section>

        <footer className="pt-6 border-t border-[var(--ink-border)] text-xs text-white/40">
          Need a deeper dive into any section? Send feedback from <Link to="/dashboard/settings" className="underline">Settings</Link>.
        </footer>
      </article>

      <aside className="hidden lg:block">
        <TOC items={TOC_ITEMS} />
      </aside>
    </div>
  );
}
